<template>
  <div class="question-list-container">

    <!-- AI回答区域 -->
  <div v-if="showAiAnswer" class="ai-answer">
    <div class="ai-answer-header">
      <span class="ai-icon">🤖</span>
      <span class="ai-label">AI回答</span>
      <button v-if="!isLoadingAiAnswer && !aiAnswer" @click="getAiAnswer" class="ai-btn">获取AI回答</button>
      <div v-if="isLoadingAiAnswer" class="ai-loading">
        <div class="loading-dots">
          <span></span>
          <span></span>
          <span></span>
        </div>
        <span>AI思考中...</span>
      </div>
    </div>
    <div v-if="aiAnswer" class="ai-answer-content">{{ aiAnswer }}</div>
  </div>

    <!-- 美化全局答案展示区域 -->
    <div v-if="globalAnswer" class="global-answer">
      <div class="global-answer-header">
        <span class="answer-icon">📝</span>
        <span class="answer-label">推荐回答</span>
      </div>
      <div class="answer-content">{{ formatAnswer(globalAnswer) }}</div>
    </div>

    <!-- 无结果状态 -->
    <div v-if="!questions || questions.length === 0" class="no-results">
      <p>没有找到相关题目</p>
      <p class="sub-text">请尝试其他搜索词或筛选条件</p>
    </div>
    
    <!-- 题目列表 -->
    <div v-else class="question-list">
      <div 
        v-for="(question) in filteredQuestions" 
        :key="question.uuid" 
        class="question-card"
        @click="viewQuestionDetail(question)"
      >
        <div class="question-header">
          <span class="question-type" :class="getTypeClass(question.subjectType)">
            {{ getQuestionTypeText(question.subjectType) }}
          </span>
        </div>
        
        <div class="question-content">
          <h3 class="question-title">{{ formatQuestionTitle(question.subjectName) }}</h3>
          
          <!-- 选择题选项 -->
          <div v-if="question.subjectType === 1 || question.subjectType === 2 && question.subjectOptions" class="question-options">
            <div 
              v-for="option in parseOptions(question.subjectOptions)" 
              :key="option.optionHead" 
              class="option-item"
              :class="{ 'correct-option': isCorrectAnswer(question.rightAnswer, option.optionHead) }"
            >
              <span class="option-head">{{ option.optionHead }}.</span>
              <span class="option-content" v-html="cleanHtml(option.optionContent)"></span>
            </div>
          </div>
          
          <!-- 判断题答案 -->
          <div v-if="question.subjectType === 3" class="question-judge">
            <span class="judge-option" :class="{ 'correct': question.rightAnswer === 'yes' }">
              <span class="judge-icon">✓</span> 正确
            </span>
            <span class="judge-option" :class="{ 'correct': question.rightAnswer === 'no' }">
              <span class="judge-icon">✗</span> 错误
            </span>
          </div>

          <!-- 答案解析 (移到这里，所有题型都显示) -->
          <div v-if="question.answer" class="answer-explanation">
            <div class="answer-label">答案解析：</div>
            <div class="answer-content">{{ formatAnswer(question.answer) }}</div>
          </div>
        </div>
      </div>
    </div>
    
    <!-- 分页控件 -->
    <div v-if="totalPages > 1" class="pagination">
      <button 
        class="pagination-btn" 
        :disabled="currentPage === 1" 
        @click="changePage(currentPage - 1)"
      >
        上一页
      </button>
      <span class="page-info">{{ currentPage }} / {{ totalPages }}</span>
      <button 
        class="pagination-btn" 
        :disabled="currentPage === totalPages" 
        @click="changePage(currentPage + 1)"
      >
        下一页
      </button>
    </div>
  </div>

  <!-- 添加登录提示弹窗 -->
  <div v-if="showLoginModal" class="login-modal">
      <div class="modal-content">
        <h3>登录提示</h3>
        <p>请先登录后再使用AI回答功能</p>
        <div class="modal-buttons">
          <button @click="cancelLogin" class="cancel-btn">取消</button>
          <button @click="confirmLogin" class="confirm-btn">确定</button>
        </div>
      </div>
    </div>
  
</template>

<script>
export default {
  name: 'QuestionList',
  props: {
    // 新增全局答案 prop
    globalAnswer: {
      type: String,
      default: ''
    },
    questions: {
      type: Array,
      default: () => [],
      validator: function(value) {
        // 验证数组中的每个元素是否具有必要的属性
        return value.every(item => {
          return item.uuid && 
                 item.subjectName && 
                 item.subjectType !== undefined &&
                 item.subjectScore !== undefined;
        });
      }
    },
    filterType: {
      type: String,
      default: '全部'
    },
    // 添加搜索查询prop
    searchQuery: {
      type: String,
      default: ''
    }
  },
  
  data() {
    return {
      currentPage: 1,
      pageSize: 10,
      questionTypes: {
        1: '选择题',
        2: '多选题',  // 修改这里，从"填空题"改为"多选题"
        3: '判断题',
        4: '简答题',
        5: '综合题'
      },
      // 添加AI回答相关状态
      aiAnswer: '',
      isLoadingAiAnswer: false,
      showAiAnswer: true,
      // 添加登录弹窗状态
      showLoginModal: false
    };
  },
  
  computed: {
    filteredQuestions() {
      // 直接返回分页数据，不进行筛选
      const start = (this.currentPage - 1) * this.pageSize;
      const end = start + this.pageSize;
      return this.questions.slice(start, end);
    },
    
    totalPages() {
      return Math.ceil(this.questions.length / this.pageSize);
    }
  },
  
  methods: {
    // 添加获取AI回答的方法
    async getAiAnswer() {
      if (!this.searchQuery || this.isLoadingAiAnswer) return;
      
      // 获取token并检查
      const token = localStorage.getItem('token');
      if (!token) {
        // 显示登录提示弹窗
        this.showLoginModal = true;
        return;
      }
      
      this.isLoadingAiAnswer = true;
      this.aiAnswer = '';
      
      try {
        let authToken = '';
        if (token) {
          // 按要求分割token，取前面部分
          authToken = token.split('|')[0];
        }
        
        // 准备请求数据
        const requestData = {
          messages: [
            {
              role: "system",
              content: "你是有用的人工智能助手，请用清晰格式回答，适当使用段落和列表，但不要使用markdown格式，并且在开头都要回答出你认为对的回答"
            },
            {
              role: "user",
              content: this.searchQuery
            }
          ],
          stream: true
        };
        
        // 发送POST请求并处理流式响应
        const response = await fetch('https://api.csid.cc/deepseek', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${authToken}`
          },
          body: JSON.stringify(requestData)
        });
        
        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
        
        // 创建读取流
        const reader = response.body.getReader();
        const decoder = new TextDecoder();
        
        // 处理流式数据
        let reading = true;
        // eslint-disable-next-line no-unmodified-loop-condition
        while (reading) {
          const { done, value } = await reader.read();
          if (done) {
            this.isLoadingAiAnswer = false;
            reading = false;
            break;
          }
          
          // 解码二进制数据
          const chunk = decoder.decode(value, { stream: true });
          
          // 处理数据块（可能包含多个事件）
          const lines = chunk.split('\n');
          for (const line of lines) {
            if (line.startsWith('data: ')) {
              try {
                const jsonData = line.substring(6); // 移除 "data: " 前缀
                if (jsonData.trim() === '[DONE]') {
                  continue; // 跳过结束标记
                }
                
                const data = JSON.parse(jsonData);
                
                if (data.choices && data.choices[0] && data.choices[0].delta && data.choices[0].delta.content) {
                  this.aiAnswer += data.choices[0].delta.content;
                }
              } catch (error) {
                console.error('解析事件数据失败:', error);
              }
            }
          }
        }
      } catch (error) {
        console.error('获取AI回答失败:', error);
        this.isLoadingAiAnswer = false;
      }
    },
    
    // 添加取消登录方法
    cancelLogin() {
      this.showLoginModal = false;
    },
    
    // 添加确认登录方法
    confirmLogin() {
      this.showLoginModal = false;
      // 跳转到登录页面
      this.$router.push('/login');
    },
    
    // 现有方法保持不变
    formatAnswer(answer) {
      if (!answer) return '';
      return answer.split('#').join('\n');
    },
    // 格式化题目标题，去除HTML标签
    formatQuestionTitle(title) {
      if (!title) return '';
      // 移除HTML标签
      return title.replace(/<[^>]*>/g, '');
    },
    
    // 清理HTML内容，保留基本格式
    cleanHtml(html) {
      if (!html) return '';
      // 这里可以使用更复杂的HTML清理逻辑，或者使用第三方库
      return html.replace(/<p>/g, '').replace(/<\/p>/g, '');
    },
    
    // 解析选项JSON字符串
    parseOptions(optionsStr) {
      if (!optionsStr) return [];
      try {
        return JSON.parse(optionsStr);
      } catch (e) {
        console.error('解析选项失败:', e);
        return [];
      }
    },
    
    // 获取题目类型文本
    getQuestionTypeText(type) {
      return this.questionTypes[type] || '未知类型';
    },
    
    // 获取题目类型样式类
    getTypeClass(type) {
      const typeMap = {
        1: 'type-choice',
        2: 'type-multiple',  // 可以考虑更新这个类名，从'type-blank'改为'type-multiple'
        3: 'type-judge',
        4: 'type-short',
        5: 'type-comprehensive'
      };
      return typeMap[type] || '';
    },
    
    // 判断是否为正确答案
    isCorrectAnswer(rightAnswer, optionHead) {
      if (!rightAnswer) return false;
      const answers = rightAnswer.split(',').map(a => a.trim());
      return answers.includes(optionHead);
    },
    
    // 查看题目详情
    viewQuestionDetail(question) {
      this.$emit('question-click', question);
    },
    
    // 切换页码
    changePage(page) {
      if (page < 1 || page > this.totalPages) {
        return;
      }
      this.currentPage = page;
      this.$emit('page-change', page);
      
      // 滚动到页面顶部
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  },
  
  watch: {
    // 当筛选条件改变时，重置页码
    filterType() {
      this.currentPage = 1;
    }
  }
};
</script>

<style scoped>
.question-list-container {
  width: 100%;
  max-width: 1200px;
  margin: 0 auto;

}

.no-results {
  text-align: center;
  padding: 40px 0;
  color: #666;
}

.no-results p {
  margin: 5px 0;
  font-size: 18px;
}

.no-results .sub-text {
  font-size: 14px;
  color: #999;
}

.question-list {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.question-card {
  background-color: #fff;
  border-radius: 12px;
  border: 1.2px solid rgb(239, 239, 239);
  box-shadow: 8px 8px 20px 0 rgba(0, 0, 0, 0.02);
  padding: 16px;
  transition: transform 0.2s, box-shadow 0.2s;

}

/* .question-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.15);
} */

.question-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 12px;
}

.question-type {
  display: inline-block;
  padding: 4px 8px;
  border-radius: 4px;
  font-size: 12px;
  font-weight: bold;
  color: #fff;
}

.type-choice {
  background-color: rgba(24, 144, 255, 0.8);
}

.type-multiple {
  background-color: rgba(82, 196, 26, 0.8);
}

.type-judge {
  background-color: rgba(250, 140, 22, 0.8);
}

.type-short {
  background-color: rgba(114, 46, 209, 0.8);
}

.type-comprehensive {
  background-color: rgba(235, 47, 150, 0.8);
}

.question-source {
  font-size: 12px;
  color: #999;
}

.question-content {
  margin-bottom: 16px;
}

.question-title {
  font-size: 16px;
  margin-bottom: 12px;
  line-height: 1.5;
  color: #333;
}

.question-options {
  margin-top: 10px;
}

.option-item {
  margin-bottom: 8px;
  display: flex;
  padding: 4px 8px;
  border-radius: 4px;
}

.correct-option {
  background-color: #f6ffed;
  color: #52c41a;
  border: 1px solid #b7eb8f;
}

.option-head {
  font-weight: bold;
  margin-right: 8px;
  min-width: 20px;
}

.option-content {
  flex: 1;
}

.question-judge {
  display: flex;
  gap: 16px;
  margin-top: 10px;
}

.judge-option {
  display: flex;
  align-items: center;
  padding: 4px 8px;
  border-radius: 4px;
  background-color: #f5f5f5;
}

.judge-option.correct {
  background-color: #f6ffed;
  border-radius: 4px;
  padding: 4px 8px;
  border: 1px solid #b7eb8f;
}

.judge-icon {
  margin-right: 4px;
}

.question-footer {
  display: flex;
  justify-content: space-between;
  align-items: center;
  font-size: 12px;
  color: #999;
}

.question-score {
  color: #ff4d4f;
  font-weight: bold;
}

.question-chapter {
  max-width: 70%;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.pagination {
  display: flex;
  justify-content: center;
  align-items: center;
  margin-top: 24px;
  gap: 16px;
}

.pagination-btn {
  padding: 6px 12px;
  border: 1px solid #d9d9d9;
  border-radius: 4px;
  background-color: #fff;
  cursor: pointer;
  transition: all 0.3s;
}

.pagination-btn:hover:not(:disabled) {
  color: #1890ff;
  border-color: #1890ff;
}

.pagination-btn:disabled {
  color: #d9d9d9;
  cursor: not-allowed;
}

.page-info {
  font-size: 14px;
  color: #666;
}

/* 添加答案样式 */
.question-answer {
  margin-top: 16px;
  padding: 12px;
  background-color: #f8f9fa;
  border-radius: 4px;
}

.answer-label {
  font-weight: bold;
  color: #333;
  margin-bottom: 4px;
}

.answer-content {
  color: #666;
  line-height: 1.5;
}

/* 移除不需要的样式 */
.question-source,
.question-chapter,
.question-score {
  display: none;
}

.answer-explanation {
  margin-top: 16px;
  padding: 12px;
  background-color: #f6ffed;
  border: 1px solid #b7eb8f;
  border-radius: 4px;
}

.answer-label {
  font-weight: bold;
  color: #333;
  margin-bottom: 8px;
}

.answer-content {
  color: #52c41a;
  line-height: 1.6;
  white-space: pre-line;
}

/* 美化全局答案样式 */
.global-answer {
  margin: 0 0 24px 0;
  padding: 16px;
  background-color: #f0f9eb;
  border: 1px solid #c2e7b0;
  border-radius: 8px;
  box-shadow: 0 2px 12px rgba(0, 0, 0, 0.08);
  transition: all 0.3s ease;
}

.global-answer:hover {
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.12);
  transform: translateY(-2px);
}

.global-answer-header {
  display: flex;
  align-items: center;
  margin-bottom: 12px;
  padding-bottom: 8px;
  border-bottom: 1px dashed #c2e7b0;
}

.answer-icon {
  font-size: 20px;
  margin-right: 8px;
}

.answer-label {
  font-size: 16px;
  font-weight: bold;
  color: #67c23a;
}

.answer-content {
  color: #606266;
  line-height: 1.8;
  white-space: pre-line;
  font-size: 15px;
  padding: 4px 8px;
  background-color: rgba(255, 255, 255, 0.7);
  border-radius: 4px;
}

/* 其他样式保持不变 */

/* 美化AI回答样式 */
.ai-answer {
  background: linear-gradient(to right, #f0f7ff, #f5f9ff);
  border-radius: 12px;
  padding: 20px;
  margin-bottom: 24px;
  box-shadow: 0 4px 16px rgba(24, 144, 255, 0.1);
  border: 1px solid rgba(24, 144, 255, 0.2);
  position: relative;
  overflow: hidden;
}

.ai-answer::before {
  /* content: '';
  position: absolute;
  top: 0;
  left: 0;
  width: 6px;
  height: 100%;
  background: linear-gradient(to bottom, #1890ff, #69c0ff);
  border-radius: 3px 0 0 3px; */
}

.ai-answer-header {
  display: flex;
  align-items: center;
  margin-bottom: 16px;
  padding-bottom: 12px;
  border-bottom: 1px dashed rgba(24, 144, 255, 0.3);
}

.ai-icon {
  /* font-size: 24px;
  margin-right: 10px;
  background: linear-gradient(45deg, #1890ff, #69c0ff);
  -webkit-background-clip: text;
  background-clip: text;
  color: transparent; */
}

.ai-label {
  font-weight: bold;
  font-size: 18px;
  color: #1890ff;
  margin-right: auto;
}

.ai-btn {
  background: linear-gradient(45deg, #1890ff, #69c0ff);
  color: white;
  border: none;
  border-radius: 8px;
  padding: 8px 16px;
  cursor: pointer;
  transition: all 0.3s;
  font-weight: 500;
  box-shadow: 0 2px 8px rgba(24, 144, 255, 0.3);
}

.ai-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(24, 144, 255, 0.4);
}

.ai-btn:active {
  transform: translateY(0);
}

.ai-loading {
  display: flex;
  align-items: center;
  color: #1890ff;
  font-size: 14px;
}

.loading-dots {
  display: flex;
  margin-right: 8px;
}

.loading-dots span {
  width: 8px;
  height: 8px;
  margin: 0 2px;
  background-color: #1890ff;
  border-radius: 50%;
  display: inline-block;
  animation: dot-flashing 1s infinite alternate;
}

.loading-dots span:nth-child(2) {
  animation-delay: 0.2s;
}

.loading-dots span:nth-child(3) {
  animation-delay: 0.4s;
}

@keyframes dot-flashing {
  0% {
    opacity: 0.2;
  }
  100% {
    opacity: 1;
  }
}

.ai-answer-content {
  line-height: 1.8;
  color: #333;
  font-size: 15px;
  background-color: rgba(255, 255, 255, 0.7);
  padding: 16px;
  border-radius: 8px;
  white-space: pre-line;
  position: relative;
}

.ai-answer-content::before {
  /* content: '"';
  position: absolute;
  top: -10px;
  left: -5px;
  font-size: 40px;
  color: rgba(24, 144, 255, 0.2);
  font-family: serif; */
}

.ai-answer-content::after {
  /* content: '"';
  position: absolute;
  bottom: -30px;
  right: -5px;
  font-size: 40px;
  color: rgba(24, 144, 255, 0.2);
  font-family: serif; */
}




/* 添加登录弹窗样式 */
.login-modal {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;
}

.modal-content {
  background-color: white;
  border-radius: 8px;
  padding: 24px;
  width: 90%;
  max-width: 400px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.2);
}

.modal-content h3 {
  margin-top: 0;
  color: #1890ff;
  font-size: 18px;
  margin-bottom: 16px;
}

.modal-content p {
  margin-bottom: 24px;
  color: #333;
  line-height: 1.5;
}

.modal-buttons {
  display: flex;
  justify-content: flex-end;
  gap: 12px;
}

.cancel-btn {
  padding: 8px 16px;
  border: 1px solid #d9d9d9;
  background-color: white;
  color: #666;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s;
}

.cancel-btn:hover {
  color: #1890ff;
  border-color: #1890ff;
}

.confirm-btn {
  padding: 8px 16px;
  background: linear-gradient(45deg, #1890ff, #69c0ff);
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.3s;
  box-shadow: 0 2px 8px rgba(24, 144, 255, 0.3);
}

.confirm-btn:hover {
  transform: translateY(-2px);
  box-shadow: 0 4px 12px rgba(24, 144, 255, 0.4);
}
</style>

